import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import eventsApi from '../api/eventsApi';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations/translations';
import { initScrollAnimations, initFirstLoadAnimation, cleanupScrollAnimations } from '../utils/scrollAnimations';
import '../styles/home.css';
import '../styles/animations.css';

const Home = () => {
  const { language } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [timerId, setTimerId] = useState(null);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [newsletterName, setNewsletterName] = useState('');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(''); // 'loading', 'success', 'error'
  const [newsletterMessage, setNewsletterMessage] = useState('');
  
  const images = [
    '/assets/Garbary25.jpg'
  ];

  // Load events from MongoDB (language-aware)
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoadingEvents(true);
        const eventsData = await eventsApi.getAllEvents(language);
        // Filter upcoming events and sort by date
        const upcomingEvents = eventsData
          .filter(event => new Date(event.date) >= new Date())
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 3); // Get first 3 upcoming events
        setEvents(upcomingEvents);
      } catch (err) {
        console.error('Failed to load events:', err);
      } finally {
        setLoadingEvents(false);
      }
    };
    loadEvents();
  }, [language]); // Reload when language changes

  // Initialize animations
  useEffect(() => {
    // Initialize first load animation
    initFirstLoadAnimation();
    
    // Initialize scroll animations
    const scrollObserver = initScrollAnimations();
    
    // Cleanup on unmount
    return () => {
      cleanupScrollAnimations(scrollObserver);
    };
  }, []);

  // Preload images
  useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.onerror = () => console.error('Failed to load image:', src);
      img.onload = () => console.log('Successfully loaded image:', src);
      img.src = src;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Main slideshow logic
  useEffect(() => {
    if (isPaused) return;

    // Clear any existing timer
    if (timerId) {
      clearTimeout(timerId);
    }

    // Start progress animation
    let progressValue = 0;
    const progressInterval = setInterval(() => {
      progressValue += 0.5;
      setProgress(Math.min(progressValue, 100));
    }, 50);

    // Set main timer for image change
    const newTimerId = setTimeout(() => {
      clearInterval(progressInterval);
      setIsTransitioning(true);
      
      setTimeout(() => {
        setCurrentImageIndex(prev => (prev + 1) % images.length);
        setProgress(0);
        setIsTransitioning(false);
      }, 300);
    }, 10000);

    setTimerId(newTimerId);

    return () => {
      clearTimeout(newTimerId);
      clearInterval(progressInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImageIndex, isPaused]);

  const goToNextImage = () => {
    if (isTransitioning) return;
    
    // Clear current timer
    if (timerId) {
      clearTimeout(timerId);
    }
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImageIndex(prev => (prev + 1) % images.length);
      setProgress(0);
      setIsTransitioning(false);
    }, 300);
  };

  const goToPreviousImage = () => {
    if (isTransitioning) return;
    
    // Clear current timer
    if (timerId) {
      clearTimeout(timerId);
    }
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
      setProgress(0);
      setIsTransitioning(false);
    }, 300);
  };

  const togglePause = () => {
    setIsPaused(prev => !prev);
  };

  // Newsletter subscription handler
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      setNewsletterStatus('error');
      setNewsletterMessage(language === 'pl' ? 'Proszę podać prawidłowy adres email.' : 'Please enter a valid email address.');
      return;
    }

    setNewsletterStatus('loading');
    setNewsletterMessage('');

    try {
      const response = await fetch('/api/subscribe-newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newsletterName,
          email: newsletterEmail,
          language: language
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setNewsletterStatus('success');
        setNewsletterMessage(data.message);
        // Clear form
        setNewsletterName('');
        setNewsletterEmail('');
        // Reset status after 5 seconds
        setTimeout(() => {
          setNewsletterStatus('');
          setNewsletterMessage('');
        }, 5000);
      } else {
        setNewsletterStatus('error');
        setNewsletterMessage(data.error || (language === 'pl' ? 'Coś poszło nie tak. Spróbuj ponownie.' : 'Something went wrong. Please try again.'));
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setNewsletterStatus('error');
      setNewsletterMessage(language === 'pl' ? 'Błąd połączenia. Spróbuj ponownie później.' : 'Connection error. Please try again later.');
    }
  };

  // Helper function to format date
  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    // Use UTC methods to avoid timezone conversion issues
    return {
      day: date.getUTCDate().toString().padStart(2, '0'),
      month: date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' })
    };
  };

  return (
    <div className="home">
        <Header />
      <section className="hero">
        <div className="hero-background">
          {images.map((image, index) => {
            const isActive = index === currentImageIndex;
            const isNext = index === (currentImageIndex + 1) % images.length;
            
            return (
              <div key={index} className={`hero-slide slide-${index} ${isActive ? 'active' : ''} ${isTransitioning && isNext ? 'transitioning' : ''}`}>
                <img 
                  src={image}
            alt="Pawilon Krzyżanowskiego" 
            className="hero-image"
          />
                <div className={`hero-slide-overlay slide-${index}-overlay`}></div>
              </div>
            );
          })}
        </div>
        <div className={`hero-text rental-text ${currentImageIndex === 0 && !isTransitioning ? 'active' : ''}`}>
          <p className="rental-subtitle">{language === 'pl' ? 'WYNAJEM CAŁEGO BUDYNKU' : 'RENT THE ENTIRE VENUE'}</p>
          <h1 className="rental-title">{language === 'pl' ? 'TWOJE WYDARZENIE,' : 'YOUR EVENT,'}</h1>
          <h1 className="rental-title-second">{language === 'pl' ? 'NASZA PRZESTRZEŃ' : 'OUR SPACE'}</h1>
          <p className="rental-description">{language === 'pl' ? 'Stwórz niezapomniane chwile w historycznych wnętrzach' : 'Create unforgettable moments in historic interiors'}</p>
          <Link to="/contact" className="rental-contact-button">
            {language === 'pl' ? 'SKONTAKTUJ SIĘ' : 'CONTACT US'}
          </Link>
        </div>
        
        <div className={`hero-navigation ${currentImageIndex === 0 ? 'dark-controls' : ''}`}>
          <div className="hero-nav-arrows">
            <img 
              src="/assets/ArrowLeft.png" 
              alt="Previous" 
              className="hero-nav-arrow" 
              onClick={goToPreviousImage}
            />
            <img 
              src="/assets/ArrowRight.png" 
              alt="Next" 
              className="hero-nav-arrow" 
              onClick={goToNextImage}
            />
            <img 
              src={isPaused ? "/assets/PlayIcon.png" : "/assets/PauseIcon.png"}
              alt={isPaused ? "Play" : "Pause"}
              className={`hero-pause-button ${isPaused ? 'paused' : ''}`}
              onClick={togglePause}
            />
          </div>
          <div className="hero-progress-bar">
            <div 
              className="hero-progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </section>
      
      <section className="second-section scroll-animate">
        <div className="second-section-content">
          <div className="second-right">
            <div className="events-header scroll-animate delay-100">
              <h2 className="events-title">{t('home.events.title', language)}</h2>
              <p className="events-subtitle">{t('home.events.subtitle', language)}</p>
            </div>
            <div className="events-list">
              {loadingEvents ? (
                <div className="event-item">
                  <p style={{ textAlign: 'center', padding: '2rem', color: '#555' }}>{t('home.events.loading', language)}</p>
                </div>
              ) : events.length === 0 ? (
                <div className="event-item">
                  <p style={{ textAlign: 'center', padding: '2rem', color: '#555' }}>{t('home.events.noEvents', language)}</p>
                </div>
              ) : (
                events.map((event, index) => {
                  const eventDate = formatEventDate(event.date);
                  const isVenueEvent = event.tickets?.available === 'venue';
                  return (
                    <div key={event._id} className="homepage-event-item">
                      <div className="homepage-event-date">
                        <span className="homepage-event-day">{isVenueEvent ? '  ' : eventDate.day}</span>
                        <span className="homepage-event-month">{isVenueEvent ? '' : eventDate.month}</span>
                      </div>
                      <div className="homepage-event-divider"></div>
                      <div className="homepage-event-details">
                        <h3 className="homepage-event-title">{event.title}</h3>
                        <p className="homepage-event-description">{event.shortDescription}</p>
                      </div>
                      <div className="homepage-event-divider"></div>
                      <div className="homepage-event-action">
                        {isVenueEvent ? (
                          <Link to="/contact" className="homepage-event-button">{language === 'pl' ? 'Skontaktuj się' : 'Contact Us'}</Link>
                        ) : (
                          <Link to="/events" className="homepage-event-button">{t('home.events.getTickets', language)}</Link>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </section>
      
      <section className="exhibition-section scroll-animate">
        <div className="exhibition-section-content">
          <div className="exhibition-left scroll-animate delay-200">
            <h2 className="exhibition-title">{t('home.about.title', language)}</h2>
            <h3 className="exhibition-subtitle">{t('home.about.subtitle', language)}</h3>
            <p className="exhibition-description">
            {t('home.about.description1', language)}
            </p>
            <p className="exhibition-description">
            {t('home.about.description2', language)}
            </p>
          </div>
          <div className="exhibition-right">
            <div className="exhibition-gallery">
              <Link to="/private-events" className="exhibition-column">
                <div className="exhibition-image-container">
                  <img src="/assets/PawilonWedding.png" alt="Requirements" className="exhibition-image" />
                </div>
                <div className="exhibition-content">
                  <div className="exhibition-text-container">
                    <span className="exhibition-text-en">{t('home.about.hosting', language)}</span>
                    <span className="exhibition-text-jp">{t('home.about.privateEvents', language)}</span>
                  </div>
                  <button className="exhibition-button">→</button>
                </div>
              </Link>
              
              <Link to="/events" className="exhibition-column">
                <div className="exhibition-image-container">
                  <img src="/assets/PawilonConference.png" alt="Preparations" className="exhibition-image" />
                </div>
                <div className="exhibition-content">
                  <div className="exhibition-text-container">
                    <span className="exhibition-text-en">{t('home.about.explore', language)}</span>
                    <span className="exhibition-text-jp">{t('home.about.theVenue', language)}</span>
                  </div>
                  <button className="exhibition-button">→</button>
                </div>
              </Link>
              
              <Link to="/the-venue" className="exhibition-column">
                <div className="exhibition-image-container">
                  <img src="/assets/Garbary25.jpg" alt="How to make" className="exhibition-image" />
                </div>
                <div className="exhibition-content">
                  <div className="exhibition-text-container">
                    <span className="exhibition-text-en">{t('home.about.the', language)}</span>
                    <span className="exhibition-text-jp">{t('home.about.pawilonExperience', language)}</span>
                  </div>
                  <button className="exhibition-button">→</button>
                </div>
              </Link>
          </div>
          </div>
        </div>
      </section>
      
      <section className="archive-section scroll-animate">
        <div className="archive-section-content">
          <div className="archive-left scroll-animate delay-300">
            <h2 className="archive-subtitle">{t('home.booking.subtitle', language)}</h2>
            <h2 className="archive-title">{t('home.booking.title', language)}</h2>
            <p className="archive-description">
            {t('home.booking.description1', language)}
            </p>
            <p className="archive-description">
            {t('home.booking.description2', language)}
            </p>
                <Link to="/private-events" className="archive-button">{t('home.booking.button', language)}</Link>
          </div>
          <div className="archive-right">
            <img 
              src="/assets/PawilonPrivateDinner.png" 
              alt="Artwork Collection" 
              className="archive-image"
            />
          </div>
        </div>
      </section>
      
      <section className="newsletter-section scroll-animate">
        <div className="newsletter-section-content">
          <div className="newsletter-left scroll-animate delay-400">
            <p className="newsletter-subtitle">{t('home.newsletter.subtitle', language)}</p>
            <h2 className="newsletter-title">{t('home.newsletter.title', language)}</h2>
            <p className="newsletter-description">
            {t('home.newsletter.description1', language)}
            </p>
            <p className="newsletter-description">
            {t('home.newsletter.description2', language)}
            </p>
          </div>
          <div className="newsletter-right">
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <div className="newsletter-input-group">
                <input 
                  type="text" 
                  placeholder={t('home.newsletter.name', language)}
                  className="newsletter-input"
                  id="newsletter-name"
                  value={newsletterName}
                  onChange={(e) => setNewsletterName(e.target.value)}
                />
                <label htmlFor="newsletter-name" className="newsletter-label">{t('home.newsletter.name', language)}</label>
              </div>
              <div className="newsletter-input-group">
                <input 
                  type="email" 
                  placeholder={t('home.newsletter.email', language)}
                  className="newsletter-input"
                  id="newsletter-email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  required
                />
                <label htmlFor="newsletter-email" className="newsletter-label">{t('home.newsletter.email', language)}</label>
              </div>
              <button 
                type="submit" 
                className="newsletter-button"
                disabled={newsletterStatus === 'loading'}
              >
                {newsletterStatus === 'loading' 
                  ? (language === 'pl' ? 'Wysyłanie...' : 'Sending...') 
                  : t('home.newsletter.subscribe', language)
                }
              </button>
              {newsletterMessage && (
                <div className={`newsletter-message ${newsletterStatus}`}>
                  {newsletterMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Home;
